'use client'

/**
 * WorkspaceLoadingShell
 *
 * Interactive shell shown while GlobalState bootstraps the
 * workspace. The composer here is a REAL textarea: users can
 * type immediately instead of rage-clicking a fake skeleton
 * input, and Enter queues the message to send once ready.
 *
 * Primary responsibilities:
 * - Render AppShellSkeleton with a live draft composer
 * - Persist the draft via workspace-bootstrap-draft
 * - Surface a recovery state when loading takes >10s
 *
 * Dependencies:
 * - AppShellSkeleton (static frame)
 * - lib/chat/workspace-bootstrap-draft (sessionStorage draft)
 */

import { useEffect, useRef, useState } from 'react'
import { ArrowUp, Loader2, X } from 'lucide-react'

import { AppShellSkeleton } from '@/components/utility/app-shell-skeleton'
import {
  readWorkspaceBootstrapDraft,
  writeWorkspaceBootstrapDraft
} from '@/lib/chat/workspace-bootstrap-draft'
import { cn } from '@/lib/utils'

// How long before "Loading your workspace" becomes a recovery
// state with retry/refresh instead of an indefinite wait.
export const WORKSPACE_LOADING_SLOW_MS = 10_000

interface WorkspaceLoadingShellProps {
  variant?: 'chat' | 'plain'
  onRetry?: () => void
}

// --------------

export function WorkspaceLoadingShell({
  variant = 'chat',
  onRetry
}: WorkspaceLoadingShellProps) {
  const [text, setText] = useState(
    () => readWorkspaceBootstrapDraft()?.text ?? ''
  )
  const [queuedSend, setQueuedSend] = useState(
    () => readWorkspaceBootstrapDraft()?.queuedSend ?? false
  )
  const [elapsedMs, setElapsedMs] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const mountedAtRef = useRef<number>(Date.now())

  const isSlow = elapsedMs >= WORKSPACE_LOADING_SLOW_MS

  useEffect(() => {
    const interval = window.setInterval(() => {
      setElapsedMs(Date.now() - mountedAtRef.current)
    }, 500)

    return () => window.clearInterval(interval)
  }, [])

  // Keep the draft recoverable even if this shell unmounts mid-load
  // (route change, retry remount). Textarea stays editable while
  // queued: editing updates the queued message, it does not cancel it.
  useEffect(() => {
    writeWorkspaceBootstrapDraft({ text, queuedSend })
  }, [text, queuedSend])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return

    event.preventDefault()
    if (text.trim()) {
      setQueuedSend(true)
      textareaRef.current?.blur()
    }
  }

  const composer = (
    <div className="pointer-events-auto w-full">
      {queuedSend ? (
        <div className="bg-primary/10 text-foreground mx-auto mb-2 flex w-full max-w-3xl items-center gap-2 rounded-xl px-3 py-2 text-sm">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          <span className="min-w-0 flex-1">
            Queued — this will send as soon as your workspace is ready.
            You can keep editing it below.
          </span>
          <button
            type="button"
            onClick={() => setQueuedSend(false)}
            className="hover:bg-primary/15 shrink-0 rounded-full p-1"
            aria-label="Cancel queued send"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : isSlow ? (
        <div className="bg-amber-500/10 mx-auto mb-2 flex w-full max-w-3xl flex-wrap items-center gap-2 rounded-xl px-3 py-2 text-sm">
          <span className="text-foreground min-w-0 flex-1">
            Taking longer than usual to load your workspace. You can
            keep typing — your draft is saved.
          </span>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="bg-background hover:bg-accent shrink-0 rounded-full border px-3 py-1 font-medium"
            >
              Retry
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-background hover:bg-accent shrink-0 rounded-full border px-3 py-1 font-medium"
          >
            Refresh page
          </button>
        </div>
      ) : null}

      <div
        className={cn(
          'border-border bg-background mx-auto flex w-full max-w-3xl items-end gap-2 rounded-2xl border px-3 py-2 shadow-sm',
          'focus-within:ring-ring focus-within:ring-2'
        )}
      >
        <textarea
          ref={textareaRef}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          autoFocus
          aria-label="Message Losi"
          placeholder="Ask anything…"
          className="text-foreground placeholder:text-muted-foreground max-h-40 min-h-9 flex-1 resize-none bg-transparent px-1 py-1.5 text-base leading-normal focus:outline-none"
        />
        <button
          type="button"
          disabled={!text.trim()}
          onClick={() => text.trim() && setQueuedSend(true)}
          aria-label={
            queuedSend ? 'Queued to send when ready' : 'Queue message to send when ready'
          }
          className="bg-primary text-primary-foreground mb-0.5 inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-opacity disabled:opacity-40"
        >
          {queuedSend ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <ArrowUp className="size-4" />
          )}
        </button>
      </div>
      <p className="text-muted-foreground mx-auto mt-2 w-full max-w-3xl px-1 text-xs">
        {queuedSend
          ? 'Workspace still loading — your message is queued.'
          : 'Workspace loading — start typing now, send when ready.'}
      </p>
    </div>
  )

  return (
    <AppShellSkeleton
      variant={variant}
      composerSlot={variant === 'chat' ? composer : undefined}
      isSlow={isSlow}
    />
  )
}
